import type { ReactNode } from 'react';
import { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionTemplate,
} from 'framer-motion';
import { cn } from '~/lib/utils';
import styles from './ScrollRevealHeadline.module.css';

type HeadingTag = 'h1' | 'h2' | 'h3';

export interface ScrollRevealHeadlineProps {
  children: ReactNode;
  as?: HeadingTag;
  className?: string;
  id?: string;
}

const MOTION_TAGS = {
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
} as const;

export function ScrollRevealHeadline({
  children,
  as = 'h2',
  className,
  id,
}: ScrollRevealHeadlineProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  const reducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 100%', 'center 80%'],
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1]);
  const blurPx = useTransform(scrollYProgress, [0, 1], [8, 0]);
  const filter = useMotionTemplate`blur(${blurPx}px)`;

  if (reducedMotion) {
    const Tag = as;
    return (
      <Tag ref={ref} id={id} className={cn(styles.root, styles.reducedMotion, className)}>
        {children}
      </Tag>
    );
  }

  const MotionTag = MOTION_TAGS[as];

  return (
    <MotionTag
      ref={ref}
      id={id}
      className={cn(styles.root, className)}
      style={{ opacity, filter }}
    >
      {children}
    </MotionTag>
  );
}

ScrollRevealHeadline.displayName = 'ScrollRevealHeadline';
